module Halogen.Styled
  ( CommonProps
  , Leaf
  , Node
  , NodeKeyed
  , Opts
  , PrettyHash(..)
  , PrintedSheet(..)
  , SheetTemplate(..)
  , StyledHTML
  , UpdateOpts(..)
  , extraClasses
  , fromHTML
  , inlineRaw
  , scopedSheetRaw
  , styleLeaf
  , styleNode
  , styleNodeKeyed
  , text
  , toHTML
  , withCtx
  ) where

import Prelude

import Control.Monad.Writer (runWriter, tell)
import Data.Foldable (foldl)
import Data.Hashable (hash)
import Data.Map (Map)
import Data.Map as Map
import Data.Newtype (class Newtype, unwrap)
import Data.Ord (abs)
import Data.Set as Set
import Data.String (Pattern(..), Replacement(..))
import Data.String as Str
import Data.Traversable (for)
import Data.Tuple.Nested (type (/\), (/\))
import Halogen (ClassName(..))
import Halogen.Component (ComponentSlot)
import Halogen.HTML (HTML)
import Halogen.HTML as HH
import Halogen.HTML.Elements.Keyed as Keyed
import Halogen.HTML.Properties as HHP

newtype StyledHTML ctx w a = StyledHTML (ctx -> StyleMap /\ HTML w a)

derive instance Functor (StyledHTML ctx w)

type StyledComponentHTML action slots m = StyledHTML (ComponentSlot slots m action) action

newtype PrintedSheet = PrintedSheet String

derive instance Newtype PrintedSheet _

newtype SheetTemplate = SheetTemplate String

derive instance Newtype SheetTemplate _

-------------------------------------------------------------------------------
-- StyleMap
-------------------------------------------------------------------------------

type StyleMap = Map PrettyHash PrintedSheet

styleMapToClassNames :: StyleMap -> Array ClassName
styleMapToClassNames styleMap = Map.keys styleMap
  # Set.toUnfoldable
  # map prettyHashToClassName

-------------------------------------------------------------------------------

fromHTML :: forall ctx w a. (ctx -> HTML w a) -> StyledHTML ctx w a
fromHTML mkHtml = StyledHTML (\ctx -> Map.empty /\ mkHtml ctx)

toHTML :: forall ctx w a. ctx -> StyledHTML ctx w a -> Array (HTML w a)
toHTML ctx stHtml = do
  let
    (styleMap /\ html) = runHTML ctx stHtml :: (StyleMap /\ HTML w a)

  let
    styleElems :: Array (String /\ HTML w a)
    styleElems = styleMap
      # Map.toUnfoldable
      # map
          ( \(hash /\ sheet) ->
              (unwrap hash /\ HH.style [] [ HH.text (unwrap sheet) ])
          )

  [ html
  , Keyed.div [ HHP.style "display: none" ] styleElems
  ]

runHTML :: forall ctx w a. ctx -> StyledHTML ctx w a -> StyleMap /\ HTML w a
runHTML ctx (StyledHTML f) = f ctx

withCtx :: forall ctx w a. (ctx -> StyledHTML ctx w a) -> StyledHTML ctx w a
withCtx mkStyledHtml = StyledHTML \ctx -> runHTML ctx (mkStyledHtml ctx)

-------------------------------------------------------------------------------
-- Node and Leaf
-------------------------------------------------------------------------------

type Node (sfc :: Type -> Type) r a =
  Array (HH.IProp (CommonProps r) a) -> Array (sfc a) -> sfc a

type NodeKeyed (sfc :: Type -> Type) r a =
  Array (HH.IProp (CommonProps r) a) -> Array (String /\ (sfc a)) -> sfc a

type Leaf (sfc :: Type -> Type) r a =
  Array (HH.IProp (CommonProps r) a) -> sfc a

leafToNode :: forall srf r a. Leaf srf r a -> Node srf r a
leafToNode leaf = \props _ -> leaf props

nodeToLeaf :: forall srf r a. Node srf r a -> Leaf srf r a
nodeToLeaf node = \props -> node props []

-------------------------------------------------------------------------------
-- CommonProps
-------------------------------------------------------------------------------

type CommonProps r =
  ( style :: String
  , class :: String
  | r
  )

-------------------------------------------------------------------------------
-- StyleSheet
-------------------------------------------------------------------------------

printStyleSheetHashed :: String -> String -> SheetTemplate -> PrettyHash /\ PrintedSheet
printStyleSheetHashed uniqueIdent name sheetTemplate = prettyHash /\ sheet
  where

  prettyHash :: PrettyHash
  prettyHash = mkPrettyHash name sheetTemplate

  sheet :: PrintedSheet
  sheet = PrintedSheet $ Str.replaceAll
    (Pattern uniqueIdent)
    (Replacement $ unwrap prettyHash)
    (unwrap sheetTemplate)

-------------------------------------------------------------------------------
-- Opts
-------------------------------------------------------------------------------

type Opts ctx r a =
  { name :: String
  , extraClasses :: Array ClassName
  , inlineStyles :: Array String
  , styleSheets :: Array (PrettyHash /\ PrintedSheet)
  , props :: Array (HH.IProp (CommonProps r) a)
  , context :: ctx
  }

combineOpts :: forall ctx r a. Opts ctx r a -> Array (UpdateOpts ctx r a) -> Opts ctx r a
combineOpts opts updateOpts = foldl (\x (UpdateOpts f) -> f x) opts updateOpts

mkDefaultOpts :: forall ctx r a. ctx -> Opts ctx r a
mkDefaultOpts ctx =
  { name: ""
  , extraClasses: []
  , styleSheets: []
  , inlineStyles: []
  , context: ctx
  , props: []
  }

newtype UpdateOpts ctx r a = UpdateOpts (Opts ctx r a -> Opts ctx r a)

instance Monoid (UpdateOpts ctx r a) where
  mempty = UpdateOpts identity

instance Semigroup (UpdateOpts ctx r a) where
  append (UpdateOpts f1) (UpdateOpts f2) = UpdateOpts (f1 >>> f2)

-------------------------------------------------------------------------------
-- styleNode and styleLeaf
-------------------------------------------------------------------------------

styleNode
  :: forall ctx r w a
   . Node (HTML w) r a
  -> Array (UpdateOpts ctx r a)
  -> Node (StyledHTML ctx w) r a
styleNode node optsUpdates = \props children -> StyledHTML \ctx -> do
  let
    (newChildren /\ styleMapsChildren) = runChildren ctx children

  let
    opts :: Opts ctx r a
    opts = combineOpts (mkDefaultOpts ctx) optsUpdates

  let
    styleMapParent :: StyleMap
    styleMapParent = Map.fromFoldable opts.styleSheets

  let
    styleMap :: StyleMap
    styleMap = Map.unions ([ styleMapParent ] <> styleMapsChildren)

  let
    newProps :: Array (HH.IProp (CommonProps r) a)
    newProps = mkProps
      { extraClasses: opts.extraClasses
      , styleMap: styleMapParent
      , inlineStyles: opts.inlineStyles
      }
  let
    html :: HTML w a
    html = node (opts.props <> newProps <> props) newChildren

  styleMap /\ html

styleNodeKeyed
  :: forall ctx r w a
   . NodeKeyed (HTML w) r a
  -> Array (UpdateOpts ctx r a)
  -> NodeKeyed (StyledHTML ctx w) r a
styleNodeKeyed node optsUpdates = \props children -> StyledHTML \ctx -> do
  let
    (newChildren /\ styleMapsChildren) = runChildrenKeyed ctx children

  let
    opts :: Opts ctx r a
    opts = combineOpts (mkDefaultOpts ctx) optsUpdates

  let
    styleMapParent :: StyleMap
    styleMapParent = Map.fromFoldable opts.styleSheets

  let
    styleMap :: StyleMap
    styleMap = Map.unions ([ styleMapParent ] <> styleMapsChildren)

  let
    newProps :: Array (HH.IProp (CommonProps r) a)
    newProps = mkProps
      { extraClasses: opts.extraClasses
      , styleMap: styleMapParent
      , inlineStyles: opts.inlineStyles
      }

  let
    html :: HTML w a
    html = node (opts.props <> newProps <> props) newChildren

  styleMap /\ html

styleLeaf
  :: forall ctx r w a
   . Leaf (HTML w) r a
  -> Array (UpdateOpts ctx r a)
  -> Leaf (StyledHTML ctx w) r a
styleLeaf leaf updateOpts = nodeToLeaf $ styleNode (leafToNode leaf) updateOpts

runChildren :: forall ctx w a. ctx -> Array (StyledHTML ctx w a) -> (Array (HTML w a) /\ Array StyleMap)
runChildren ctx children =
  runWriter $
    for children \styledHtml -> do
      let styleMap /\ html = runHTML ctx styledHtml
      tell [ styleMap ]
      pure html

runChildrenKeyed :: forall ctx w a. ctx -> Array (String /\ (StyledHTML ctx w a)) -> (Array (String /\ (HTML w a)) /\ Array StyleMap)
runChildrenKeyed ctx children =
  runWriter $
    for children \(key /\ styledHtml) -> do
      let styleMap /\ html = runHTML ctx styledHtml
      tell [ styleMap ]
      pure (key /\ html)

mkProps
  :: forall r a
   . { extraClasses :: Array ClassName
     , styleMap :: StyleMap
     , inlineStyles :: Array String
     }
  -> Array (HH.IProp (CommonProps r) a)
mkProps cfg =
  do
    let
      classNames :: Array ClassName
      classNames = cfg.extraClasses <> styleMapToClassNames cfg.styleMap

    let
      inlineStyles :: String
      inlineStyles = Str.joinWith ";" cfg.inlineStyles

    (if classNames == [] then [] else [ HHP.classes classNames ])
      <>
        (if inlineStyles == "" then [] else [ HHP.style inlineStyles ])

-------------------------------------------------------------------------------
-- PrettyHash
-------------------------------------------------------------------------------

newtype PrettyHash = PrettyHash String

derive newtype instance Eq PrettyHash
derive newtype instance Ord PrettyHash
derive instance Newtype PrettyHash _

prettyHashToClassName :: PrettyHash -> ClassName
prettyHashToClassName (PrettyHash str) = ClassName str

mkPrettyHash :: String -> SheetTemplate -> PrettyHash
mkPrettyHash name (SheetTemplate val) =
  let
    numericHash :: Int
    numericHash = hash val

    stringHash :: String
    stringHash =
      if numericHash < 0 then
        "a" <> (show $ abs numericHash)
      else
        "b" <> (show numericHash)

    prefix = if name == "" then "" else name <> "-"

  in
    PrettyHash (prefix <> stringHash)

-------------------------------------------------------------------------------
-- Convenience API
-------------------------------------------------------------------------------

extraClasses :: forall ctx r a. Array String -> UpdateOpts ctx r a
extraClasses cls = UpdateOpts \opts -> opts
  { extraClasses = opts.extraClasses <> map ClassName cls
  }

type Name = String

scopedSheetRaw :: forall ctx r a. Name -> SheetTemplate -> UpdateOpts ctx r a
scopedSheetRaw replacer sheetTemplate = UpdateOpts \opts -> opts
  { styleSheets = opts.styleSheets <> [ printStyleSheetHashed replacer opts.name sheetTemplate ]
  }

inlineRaw :: forall ctx r a. String -> UpdateOpts ctx r a
inlineRaw inlineStr = UpdateOpts \opts -> opts
  { inlineStyles = opts.inlineStyles <> [ inlineStr ]
  }

text :: forall ctx w a. String -> StyledHTML ctx w a
text str = fromHTML \_ -> HH.text str
